// util
import { get } from 'lodash';
import { pollRepeatedly } from 'api/shared';
import {
  hankifyScriptPlanDetails,
  generateHankAsyncZoneGeneration,
  generateHankAsyncScriptQuery
} from './util/graphQl';
import {
  parseScriptRecommendationsJobId,
  parseScriptRecommendations,
  parseScriptRecommendationsPollInterval,
  parseAdvancedScriptAvailabilityReadiness,
  parseAdvancedScriptAvailabilityPollInterval
} from './hankParser';
import networkAdapter from '../adapters/network';
import { hankFetcher } from 'util/hank';
import { trackError } from 'util/error';
import { HANK_POLLING_TIMEOUT } from 'constants/advanced';

const fetchHank = async ({ query }) => {
  const { data, errors } = await hankFetcher({ query });
  const isProcessPending = Number.isInteger(
    data?.asyncRecommendation?.status?.waitTime || data?.targetAvailableData?.updateStatus?.waitTime
  );
    
  if (errors && !isProcessPending) {
    const error = errors.find((error) => error?.['error-code']);
    const hankError = new TypeError(`Hank error with code ${error['error-code']}: ${error.message}`);
    
    trackError(hankError, { query, errors });
    throw hankError;
  }
  return { data };
};

export const fetchAdvancedScriptAvailability = ({ fieldUuid, abortIf }) => {
  const query = `
    {
      targetAvailableData(target: {id:"${fieldUuid}", type:field }, checkForUpdate:true ) {
        updateStatus{ jobId message waitTime }
        recommendations(types:[seeding]) { status },
        cciLayers { season },
        plantingLayers { season, crop{id, legacyId} },
        yieldLayers { season, crop{id, legacyId}, avgYield{q, u} }
      }
    }`;

  return pollRepeatedly({
    callToPoll: () => fetchHank({ query }),
    pollUntil: result => parseAdvancedScriptAvailabilityReadiness(result),
    parseInterval: result => parseAdvancedScriptAvailabilityPollInterval(result),
    abortIf,
    timeLimit: HANK_POLLING_TIMEOUT
  });
};

/**
 * @param targetYield Unit value of yield/area type
 * @param seedCost Unit value of usd/volume type
 * @param grainPrice Unit value of usd/volume type
 */
export const fetchScriptRecommendationStatus = ({ ...planDetails }) => {
  // For polling, see fetchScriptRecommendations, which calls this

  const hankifiedDetails = hankifyScriptPlanDetails(planDetails);
  const query = generateHankAsyncScriptQuery(hankifiedDetails);

  return fetchHank({ query });
};

/**
 * @param targetYield Unit value of yield/area type
 * @param seedCost Unit value of usd/volume type
 * @param grainPrice Unit value of usd/volume type
 */
export const fetchScriptRecommendations = ({ abortIf, ...planDetails }) => {
  const fetcher = results => {
    const jobId = parseScriptRecommendationsJobId(results);
    return fetchScriptRecommendationStatus({ ...planDetails, jobId });
  };

  return pollRepeatedly({
    callToPoll: fetcher,
    pollUntil: (result) => {
      const recommendation = parseScriptRecommendations(result);
      const features = get(recommendation, 'features', []);
      return features && features.length;
    },
    parseInterval: result => parseScriptRecommendationsPollInterval(result),
    abortIf,
    timeLimit: HANK_POLLING_TIMEOUT
  });
};

export const fetchStartZoneGeneration = ({ ...planDetails }) => {
  // TODO -- change product legacyId to id after we support loading in product UUIDs.
  const hankifiedDetails = hankifyScriptPlanDetails(planDetails);
  const query = generateHankAsyncZoneGeneration(hankifiedDetails);

  return fetchHank({ query });
};

/** Fetch Harvest analytics, including average yield data. TODO: 5.7.3 It seems possible that the yield data returned
    from Hank will be updated to use data from Rogue, and at that time we can delete the direct calls to Rogue including
    code below. See CFVWEB-193 for context, especially comments to see if action was taken along
    these lines. */
export function fetchHarvestAdjustedYields({ userId, operationUuid, fieldUuid, isOlEnabled }) {
  const legacyOptions = {
    url: '/api/rogue/v3/postharvestadjustedyield/',
    useClimateAuthHeaders: false, // Prevents a CORS error.
    method: 'POST',
    redirectsToLoginOnAuthFail: false,
    headers: {
      'x-user-id': userId
    },
    body: JSON.stringify({
      fieldUUIDs: [fieldUuid]
    })
  };

  const graphRequest = {
    userId,
    query: `{
      operation(id:"${operationUuid}",) {
        uuid: id
        name
        fields {
          uuid: id
          legacyEvents(type: HARVEST) {
            type
            ... on LegacyHarvestEvent {
              yieldAverage
            }
          }
        }
      }
    }
      `
  };
  return networkAdapter({
    legacyOptions,
    options: graphRequest,
    useOL: isOlEnabled,
    transformResponse: (data) => {
      const legacyTransformation = ({ data }) => ({
        averageYield: (
          data.operation
          && data.operation.fields
          && data.operation.fields.find(({ uuid }) => uuid === fieldUuid)
        )
          ? (
            // only need the averageYield field from legacy API, so the new OL graph responds with the minimal
            data.operation.fields.find(({ uuid }) => uuid === fieldUuid).averageYield
          ) : null 
      });
      return isOlEnabled ? legacyTransformation(data) : data; 
    }
  }).catch((err) => {
    if (err.status === 400) { // Rogue says user doesn't have access (Shared field)
      return Promise.resolve(null); // Null = no access, an expected condition.
    } else {
      throw err;
    }
  });
}
